import Avatar from "@/components/base/Avatar";
import Badge from "@/components/base/Badge";
import { EmptyState } from "@/components/base/StateViews";
import { actionMeta, dayKey, dayLabel, deviceIcon, moduleIcon, timeLabel } from "@/utils/activity";
import type { ActivityLogEntry } from "@/types";
import { plural } from "@/utils/format";

interface ActivityTimelineProps {
  entries: ActivityLogEntry[];
  /** Super-administrateur : nom de l'école de chaque action (journal de toutes les écoles). */
  schoolNames?: Record<string, string>;
}

function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(" ");
  return { firstName, lastName };
}

/** Regroupe les entrées par journée, en conservant l'ordre décroissant. */
function groupByDay(entries: ActivityLogEntry[]): { key: string; items: ActivityLogEntry[] }[] {
  const groups: { key: string; items: ActivityLogEntry[] }[] = [];
  entries.forEach((entry) => {
    const key = dayKey(entry.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(entry);
    else groups.push({ key, items: [entry] });
  });
  return groups;
}

export default function ActivityTimeline({ entries, schoolNames }: ActivityTimelineProps) {
  if (entries.length === 0) {
    return (
      <EmptyState
        icon="ri-history-line"
        title="Aucune action à afficher"
        message="Aucune action ne correspond aux filtres sélectionnés pour cette période."
      />
    );
  }

  const groups = groupByDay(entries);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="mb-3 flex items-center gap-3">
            <h4 className="text-sm font-semibold font-heading text-foreground-800">
              <a href="#activity-today" className="cursor-pointer">{dayLabel(group.key)}</a>
            </h4>
            <span className="h-px flex-1 bg-background-200"></span>
            <span className="text-xs text-foreground-400 whitespace-nowrap">
              {plural(group.items.length, "action")}
            </span>
          </div>

          <div className="relative space-y-2.5 pl-1">
            {group.items.map((entry) => {
              const meta = actionMeta[entry.action];
              const { firstName, lastName } = splitName(entry.actorName);
              return (
                <div
                  key={entry.id}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-background-300 sm:flex-row sm:items-center"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span
                      className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                        meta.tone === "primary"
                          ? "bg-primary-100 text-primary-700"
                          : meta.tone === "accent"
                            ? "bg-accent-100 text-accent-700"
                            : meta.tone === "secondary"
                              ? "bg-secondary-100 text-secondary-700"
                              : meta.tone === "success"
                                ? "bg-primary-50 text-primary-700"
                                : meta.tone === "danger"
                                  ? "bg-accent-50 text-accent-700"
                                  : "bg-background-100 text-foreground-600"
                      }`}
                    >
                      <i className={`${meta.icon} text-lg`}></i>
                    </span>

                    <div className="min-w-0">
                      <p className="text-sm leading-snug text-foreground-900">
                        {entry.description}
                      </p>
                      {entry.target && (
                        <p className="mt-1 text-xs text-foreground-500">
                          <strong className="font-medium text-foreground-700">{entry.target}</strong>
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={`${moduleIcon(entry.module)} text-xs`}></i>
                          </span>
                          {entry.module}
                        </span>
                        {schoolNames && (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-building-2-line text-xs"></i>
                            </span>
                            {entry.schoolId ? schoolNames[entry.schoolId] ?? `École #${entry.schoolId}` : "Plateforme"}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5" title={entry.ip ? `Adresse IP : ${entry.ip}` : undefined}>
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={`${deviceIcon(entry.device)} text-xs`}></i>
                          </span>
                          {/* Anciennes entrées : seule l'adresse IP a été enregistrée. */}
                          {entry.device ? (
                            <>
                              {entry.device}
                              {entry.ip && <span className="text-foreground-400 tabular-nums">· {entry.ip}</span>}
                            </>
                          ) : (
                            entry.ip || "—"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                    <div className="flex items-center gap-2">
                      <Avatar firstName={firstName} lastName={lastName} size="sm" />
                      <div className="text-right leading-tight">
                        <p className="text-xs font-medium text-foreground-800 whitespace-nowrap">
                          {entry.actorName}
                        </p>
                        <p className="text-xs text-foreground-400 whitespace-nowrap">
                          {entry.actorRole}
                        </p>
                      </div>
                      <span className="ml-1 text-xs font-medium tabular-nums text-foreground-500 whitespace-nowrap">
                        {timeLabel(entry.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}